import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/Spinner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { parseRecipesCsv, generateRecipesCsvTemplate } from '@/utils/csvUtils';
import { useRecipes } from '@/contexts/RecipesContext';
import { ArrowLeft, Upload, Download, FileCheck, AlertCircle, Clock, HelpCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ImportRecipes = () => {
  const navigate = useNavigate();
  const { addRecipe } = useRecipes();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [summary, setSummary] = useState<{ success: number; failed: number } | null>(null);
  const [detailedLogs, setDetailedLogs] = useState<string[]>([]);
  
  const { isAdmin } = useAuth();
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      console.log(`Selected file: ${selectedFile.name} (${selectedFile.size} bytes)`);
    }
  };

  const handleDownloadTemplate = () => {
    const template = generateRecipesCsvTemplate();
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recetas_plantilla.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Plantilla descargada correctamente');
    
    addLog('Plantilla CSV descargada con instrucciones detalladas');
  };

  const addLog = (message: string) => {
    setDetailedLogs(prev => [...prev, message]);
    console.log(`[Import] ${message}`);
  };

  const clearLogs = () => {
    setDetailedLogs([]);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }

    setImporting(true);
    setSummary(null);
    clearLogs();
    let successCount = 0;
    let failedCount = 0;

    try {
      addLog('Iniciando proceso de importación...');
      
      const reader = new FileReader();
      
      const text = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = (e) => reject(new Error('Error al leer el archivo'));
        reader.readAsText(file, 'UTF-8');
      });
      
      addLog(`Archivo cargado: ${text.length} caracteres`);
      
      const recipes = parseRecipesCsv(text);
      addLog(`Se han analizado ${recipes.length} recetas del CSV`);
      
      if (recipes.length === 0) {
        toast.error('No se pudieron encontrar recetas válidas en el archivo CSV');
        addLog('❌ No se encontraron recetas válidas en el archivo');
        setImporting(false);
        return;
      }
      
      setProgress({ current: 0, total: recipes.length });
      
      for (let i = 0; i < recipes.length; i++) {
        try {
          const recipe = recipes[i];
          addLog(`Procesando receta ${i + 1}/${recipes.length}: ${recipe.title} (${recipe.ingredients.length} ingredientes, ${recipe.instructions.length} instrucciones)`);
          
          // Validar que la receta tenga los campos mínimos necesarios
          if (!recipe.title) {
            throw new Error('La receta no tiene título');
          }
          
          if (!Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) {
            throw new Error('La receta no tiene ingredientes o el formato es incorrecto');
          }
          
          if (!Array.isArray(recipe.instructions) || recipe.instructions.length === 0) {
            throw new Error('La receta no tiene instrucciones o el formato es incorrecto');
          }
          
          // Aumentado el tiempo de espera entre recetas para evitar problemas de concurrencia
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          try {
            await addRecipe(recipe);
            addLog(`✅ Receta añadida correctamente: ${recipe.title}`);
            successCount++;
          } catch (error) {
            // Extract error message from Supabase error object if possible
            let errorMessage = 'Error desconocido';
            if (error && typeof error === 'object') {
              if ('message' in error) {
                errorMessage = error.message as string;
              } else if ('error' in error) {
                errorMessage = (error.error as string) || 'Error de Supabase';
              } else if ('code' in error) {
                // Handle specific Supabase error codes
                const code = error.code as string || '';
                if (code === 'PGRST204') {
                  errorMessage = 'Error de columna: El esquema de la base de datos no coincide con los datos enviados';
                } else if (code.includes('23502')) {
                  errorMessage = 'Campo obligatorio no proporcionado';
                } else if (code.includes('22P02')) {
                  errorMessage = 'Tipo de dato incorrecto';
                } else {
                  errorMessage = `Error de base de datos (${code})`;
                }
              }
            }
            
            addLog(`❌ Error al importar receta ${i + 1}: ${errorMessage}`);
            console.error(`Error importing recipe ${i + 1}:`, error);
            failedCount++;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
          addLog(`❌ Error al importar receta ${i + 1}: ${errorMessage}`);
          console.error(`Error importing recipe ${i + 1}:`, error);
          failedCount++;
        }
        
        setProgress({ current: i + 1, total: recipes.length });
      }
      
      setSummary({ success: successCount, failed: failedCount });
      
      if (successCount > 0) {
        toast.success(`Importación completada: ${successCount} recetas importadas correctamente`);
      }
      
      if (failedCount > 0) {
        toast.error(`${failedCount} recetas no se pudieron importar`);
      }
      
      addLog('Proceso de importación finalizado');
      
      if (successCount > 0) {
        addLog('Redirigiendo a la página de recetas en 3 segundos...');
        setTimeout(() => {
          navigate('/admin/recetas');
        }, 3000);
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      addLog(`❌ Error general de importación: ${errorMessage}`);
      console.error('Error importing recipes:', error);
      toast.error('Error al importar recetas. Verifica el formato del archivo.');
    } finally {
      setImporting(false);
    }
  };

  if (!isAdmin()) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-grow py-12 px-4 bg-gray-50">
        <div className="container mx-auto max-w-3xl">
          <Button 
            variant="ghost" 
            className="mb-6 flex items-center gap-2 text-vegana-primary hover:bg-transparent hover:text-vegana-accent"
            onClick={() => navigate('/admin/recetas')}
          >
            <ArrowLeft size={16} />
            Volver a Recetas
          </Button>

          <h1 className="text-3xl font-bold text-vegana-primary mb-8">
            Importar Recetas en Lote
          </h1>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>¡Nuevo formato ultra simple!</CardTitle>
                <CardDescription>
                  Ahora puedes importar recetas de la forma más sencilla posible, sin complicaciones.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-700">Formato ultra simple</AlertTitle>
                  <AlertDescription className="text-blue-600">
                    Simplemente escribe "Ingredientes" y lista tus ingredientes, luego "Instrucciones" y lista 
                    tus pasos. ¡Sin formatos complicados!
                  </AlertDescription>
                </Alert>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="format">
                    <AccordionTrigger className="text-vegana-primary font-medium">
                      <div className="flex items-center gap-2">
                        <HelpCircle size={16} />
                        <span>Ejemplos de formato ultra simple</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm">
                      <div className="space-y-4 pt-2">
                        <div className="bg-gray-50 p-3 rounded border">
                          <h4 className="font-medium">Ingredientes:</h4>
                          <p className="mt-1 text-gray-600">Simplemente escribe la palabra "Ingredientes" seguida de los ingredientes:</p>
                          <p className="mt-2 text-xs font-mono bg-gray-100 p-2 rounded overflow-x-auto">
                            Ingredientes<br/>
                            200g Tofu<br/>
                            1 Zanahoria<br/>
                            1/2 Cebolla
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-3 rounded border">
                          <h4 className="font-medium">Instrucciones:</h4>
                          <p className="mt-1 text-gray-600">Simplemente escribe la palabra "Instrucciones" seguida de los pasos:</p>
                          <p className="mt-2 text-xs font-mono bg-gray-100 p-2 rounded overflow-x-auto">
                            Instrucciones<br/>
                            1. Cortar el tofu en cubos.<br/>
                            2. Pelar y rallar la zanahoria.<br/>
                            3. Mezclar todo.
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-3 rounded border">
                          <h4 className="font-medium">Etiquetas:</h4>
                          <p className="mt-1 text-gray-600">Simplemente separadas por comas:</p>
                          <p className="mt-2 text-xs font-mono bg-gray-100 p-2 rounded overflow-x-auto">
                            vegano, proteína, rápido
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Consejo importante</AlertTitle>
                  <AlertDescription>
                    Descarga la nueva plantilla para ver ejemplos del formato ultra simple. Este nuevo formato es mucho más fácil de usar.
                  </AlertDescription>
                </Alert>
                
                <Button onClick={handleDownloadTemplate} className="w-full md:w-auto flex items-center gap-2">
                  <Download size={16} />
                  Descargar Plantilla CSV
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Subir archivo CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Input 
                    type="file" 
                    accept=".csv"
                    onChange={handleFileChange} 
                    className="hidden" 
                    id="csv-upload"
                    disabled={importing}
                  />
                  <label 
                    htmlFor="csv-upload" 
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload className="h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-lg font-medium mb-1">
                      {file ? file.name : 'Haz clic para seleccionar un archivo'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {file ? `${(file.size / 1024).toFixed(2)} KB` : 'Solo archivos CSV'}
                    </p>
                  </label>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col">
                <Button 
                  onClick={handleImport} 
                  className="w-full bg-vegana-primary hover:bg-vegana-primary/90"
                  disabled={!file || importing}
                >
                  {importing ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span>Importando... {progress.current}/{progress.total}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FileCheck size={16} />
                      <span>Importar Recetas</span>
                    </div>
                  )}
                </Button>
                
                {importing && progress.total > 0 && (
                  <div className="w-full mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-vegana-secondary h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${(progress.current / progress.total) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {Math.round((progress.current / progress.total) * 100)}% completado
                    </p>
                  </div>
                )}
              </CardFooter>
            </Card>

            {detailedLogs.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <span>Registro de importación</span>
                    <Button variant="ghost" size="sm" onClick={clearLogs}>Limpiar</Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-md font-mono text-xs h-60 overflow-y-auto">
                    {detailedLogs.map((log, index) => (
                      <div key={index} className="mb-1">
                        <span className="text-gray-400">{new Date().toLocaleTimeString()}: </span>
                        <span dangerouslySetInnerHTML={{ __html: log.replace(/✅/g, '<span class="text-green-400">✅</span>').replace(/❌/g, '<span class="text-red-400">❌</span>') }} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Resultados de la Importación</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                      <p className="text-green-700 text-sm">Recetas importadas</p>
                      <p className="text-2xl font-bold text-green-700">{summary.success}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                      <p className="text-red-700 text-sm">Recetas fallidas</p>
                      <p className="text-2xl font-bold text-red-700">{summary.failed}</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/admin/recetas')}
                    >
                      Ver todas las recetas
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ImportRecipes;
